<template>
  <div class="row mt-4">
    <div class="col-12">
      <div class="card ficha-listado">
        <div class="px-4 pb-4 card-body">

          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div>
              <h6 class="mb-0">Landings más efectivas</h6>
              <p class="text-xs opacity-7 mb-0">
                Gráfico agrupado por template · tabla con el detalle de cada landing · mín. 5 sesiones
              </p>
            </div>
            <div class="btn-group btn-group-sm" role="group">
              <button
                v-for="m in metricas"
                :key="m.key"
                class="btn btn-sm mb-0"
                :class="metrica === m.key ? 'btn-activo' : 'btn-inactivo'"
                @click="metrica = m.key"
              >{{ m.label }}</button>
            </div>
          </div>

          <div v-if="landings.length">
            <!-- Gráfico agrupado por template -->
            <div class="land-chart mb-4">
              <QuantyBarChart
                id="pub-landings-chart"
                :labels="chartLabels"
                :data="chartData"
                :title="metricaActual.label + ' (por template)'"
                :value-format="metricaActual.format"
                :tooltip-suffix="metricaActual.suffix"
                :use-flags="false"
                :max-items="12"
                :integer-ticks="metricaActual.integer"
                :max-bar-size="18"
                :horizontal="true"
              />
            </div>

            <!-- Tabla individual por landing -->
            <div class="land-table-wrap">
              <table class="table table-flush table-listado table-empirulada">
                <thead class="thead-light">
                  <tr>
                    <th class="th-sort" :class="thClass('template')" @click="setOrden('template')">Landing</th>
                    <th class="th-sort" :class="thClass('tipster_nombre')" @click="setOrden('tipster_nombre')">Tipster</th>
                    <th class="text-center th-sort" :class="thClass('sesiones')" @click="setOrden('sesiones')">Sesiones</th>
                    <th class="text-center th-sort" :class="thClass('leads')" @click="setOrden('leads')">Leads</th>
                    <th class="text-center th-sort" :class="thClass('pct_conversion')" @click="setOrden('pct_conversion')" title="Verde ≥40% · Naranja ≥20% · Rojo &lt;20%">Conversión (%)</th>
                    <th class="text-center sin-orden">Preview</th>
                  </tr>
                </thead>
                <tbody class="text-sm">
                  <tr v-for="row in itemsTabla" :key="row.slug_publico">
                    <td>
                      <div class="template-nombre">{{ row.template }}</div>
                      <div class="template-campana">{{ row.campana_nombre }}</div>
                    </td>
                    <td class="td-tipster">{{ row.tipster_nombre }}</td>
                    <td class="text-center">{{ row.sesiones }}</td>
                    <td class="text-center">
                      <span class="leads-badge">{{ row.leads }}</span>
                    </td>
                    <td class="text-center">
                      <span class="pct-badge" :class="pctClass(row.pct_conversion)">
                        {{ row.pct_conversion }}%
                      </span>
                    </td>
                    <td class="text-center">
                      <a :href="'/l/' + row.slug_publico" target="_blank" class="btn-preview">
                        Vista previa
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div v-if="!landings.length" class="sin-datos-periodo">
            Sin suficientes datos de landing para este periodo (mín. 5 sesiones por landing)
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QuantyBarChart from "@/components/charts/QuantyBarChart.vue";

export default {
  name: "PanelPublicistaLandingsEfectivas",
  components: { QuantyBarChart },
  props: {
    landings: { type: Array, default: () => [] },
  },
  data() {
    return {
      metrica: "leads",
      sortCol: "leads",
      sortDir: "desc",
      metricas: [
        { key: "leads",          label: "Leads",      format: "number", suffix: " leads", integer: true },
        { key: "pct_conversion", label: "Conversión", format: "number", suffix: "%",      integer: false },
        { key: "sesiones",       label: "Sesiones",   format: "number", suffix: "",       integer: true },
      ],
    };
  },
  computed: {
    metricaActual() {
      return this.metricas.find(m => m.key === this.metrica) || this.metricas[0];
    },
    // Agrupado por template (suma de sesiones + leads, pct recalculado)
    itemsPorTemplate() {
      const key = this.metrica;
      const map = {};
      for (const r of this.landings) {
        const tpl = r.template;
        if (!map[tpl]) map[tpl] = { template: tpl, sesiones: 0, leads: 0 };
        map[tpl].sesiones += Number(r.sesiones) || 0;
        map[tpl].leads    += Number(r.leads)    || 0;
      }
      for (const t of Object.values(map)) {
        t.pct_conversion = t.sesiones > 0
          ? Math.round((t.leads / t.sesiones) * 1000) / 10
          : 0;
      }
      return Object.values(map)
        .sort((a, b) => (b[key] || 0) - (a[key] || 0))
        .filter(r => Number(r[key]) > 0)
        .slice(0, 12);
    },
    chartLabels() { return this.itemsPorTemplate.map(r => r.template); },
    chartData()   { return this.itemsPorTemplate.map(r => Number(r[this.metrica]) || 0); },
    // Tabla: landings individuales ordenados por columna seleccionada
    itemsTabla() {
      const col = this.sortCol;
      const dir = this.sortDir === "asc" ? 1 : -1;
      const isNum = ["sesiones", "leads", "pct_conversion"].includes(col);
      return [...this.landings].sort((a, b) => {
        const av = isNum ? (Number(a[col]) || 0) : (a[col] || "").toLowerCase();
        const bv = isNum ? (Number(b[col]) || 0) : (b[col] || "").toLowerCase();
        return av < bv ? -dir : av > bv ? dir : 0;
      });
    },
  },
  methods: {
    pctClass(pct) {
      const n = Number(pct);
      if (n >= 40) return "pct-alta";
      if (n >= 20) return "pct-media";
      return "pct-baja";
    },
    setOrden(col) {
      if (this.sortCol === col) {
        this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
      } else {
        this.sortCol = col;
        this.sortDir = ["sesiones", "leads", "pct_conversion"].includes(col) ? "desc" : "asc";
      }
    },
    thClass(col) {
      if (this.sortCol !== col) return "";
      return this.sortDir === "asc" ? "asc" : "desc";
    },
  },
};
</script>

<style scoped>
.land-chart     { width: 100%; }
.land-table-wrap{ width: 100%; overflow-x: auto; }

.template-nombre { font-size: 13px; font-weight: 600; }
.template-campana{ font-size: 11px; color: var(--q-text-muted); }

.td-tipster { font-size: 12px; color: var(--q-text-muted); white-space: nowrap; }

.leads-badge {
  display: inline-block;
  background: rgba(245,166,35,.15);
  color: var(--q-accent);
  font-weight: 700;
  font-size: 12px;
  border-radius: 6px;
  padding: 2px 8px;
}

.pct-badge {
  display: inline-block;
  font-weight: 700;
  font-size: 12px;
  border-radius: 6px;
  padding: 2px 8px;
}
.pct-alta  { background: rgba(40,199,111,.15); color: #28c76f; }
.pct-media { background: rgba(245,166,35,.15);  color: #f5a623; }
.pct-baja  { background: rgba(234,84,85,.12);   color: #ea5455; }

.btn-preview {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: var(--q-accent);
  border: 1px solid rgba(245,166,35,.4);
  border-radius: 6px;
  padding: 2px 10px;
  text-decoration: none;
  white-space: nowrap;
  transition: background .15s;
}
.btn-preview:hover {
  background: rgba(245,166,35,.12);
  color: var(--q-accent);
}

.th-sort {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.th-sort:hover { color: var(--q-accent); }

.btn-activo  { background: var(--q-accent); color: #fff; border-color: var(--q-accent); }
.btn-inactivo{ background: transparent; color: var(--q-text-muted); border-color: var(--q-border-md); }
.btn-inactivo:hover { background: var(--q-card-alt); }
.sin-datos-periodo { text-align:center; padding:32px 0; color:var(--q-text-muted); font-size:13px; opacity:.7; }
</style>
